import { getFinancialYearLabels } from "../../brsrFinancialYearLabels";
import { SHEET_NAME as OTHER_RETIREMENT_BENEFITS_SHEET } from "./otherRetirementBenefitsSheet";
import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_VALUE_END,
  COL_VALUE_START,
  SUBHEADER_FILL,
  TABLE_HEADER_FILL,
  addMembershipTable,
  addPerformanceTable,
  addQuestionRow,
  addSectionHeader,
  addTextOrDetailsRow,
  addTrainingTable,
  borderCell,
  addWellBeingBlock,
  formatNumber,
  formatPercent,
  mergeRow,
  safeText,
  setCell,
  setPrinciple3ColumnWidths,
  toYesNo,
} from "./principle3SheetHelpers";

const PRINCIPLE_TITLE =
  "Businesses should respect and promote the well-being of all employees, including those in their value chains.";

const RETIREMENT_ROWS = [
  ["PF", "pf"],
  ["Gratuity", "gratuity"],
  ["ESI", "ESI"],
];

const GRIEVANCE_SEGMENTS = [
  ["Permanent Employees", "permanentEmployees"],
  ["Other than Permanent Employees", "otherThanPermanentEmployees"],
  ["Permanent Workers", "permanentWorkers"],
  ["Other than Permanent Workers", "otherThanPermanentWorkers"],
];

const SAFETY_INCIDENTS = [
  ["Lost Time Injury Frequency Rate (LTIFR) (per one million-person hours worked)", "lostTimeInjuryFrequencyRate"],
  ["Total recordable work-related injuries", "totalRecordableInjuries"],
  ["No. of fatalities", "numberOfFatalities"],
  ["High consequence work-related injuries or ill-health (excluding fatalities)", "highConsequenceInjuries"],
];

function addRetirementTable(ws, row, essential, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "2. Details of retirement benefits, for Current FY and Previous Financial Year.",
    "",
    { fullWidth: true }
  );

  mergeRow(ws, row, COL_D, COL_D, "Benefits", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Employees covered (%)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Workers covered (%)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "Deducted (Y/N/NA)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Employees covered (%)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "Workers covered (%)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "Deducted (Y/N/NA)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const retirement = essential?.retirementBenefits || {};

  RETIREMENT_ROWS.forEach(([label, key]) => {
    const current = retirement?.currentFinancialYear?.[key] || {};
    const previous = retirement?.previousFinancialYear?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatPercent(current?.employeesCovered), { align: "center" });
    setCell(ws, row, COL_F, formatPercent(current?.workersCovered), { align: "center" });
    setCell(ws, row, COL_G, safeText(current?.deductedDeposited), { align: "center" });
    setCell(ws, row, COL_H, formatPercent(previous?.employeesCovered), { align: "center" });
    setCell(ws, row, COL_I, formatPercent(previous?.workersCovered), { align: "center" });
    setCell(ws, row, COL_J, safeText(previous?.deductedDeposited), { align: "center" });
    row += 1;
  });

  setCell(ws, row, COL_D, "Others - please specify", { align: "left" });
  mergeRow(ws, row, COL_E, COL_J, "View Details", {
    align: "center",
    link: `#'${OTHER_RETIREMENT_BENEFITS_SHEET}'!D3`,
  });
  row += 1;

  return row;
}

function addReturnRetentionTable(ws, row, block, title) {
  row = addQuestionRow(ws, row, title, "", { fullWidth: true, bold: true });
  mergeRow(ws, row, COL_D, COL_D, "Gender", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_E, "Return to work rate", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Retention rate", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  [["Male", "male"], ["Female", "female"], ["Total", "total"]].forEach(([label, key]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatPercent(block?.[key]?.returnToWorkRate), { align: "center" });
    setCell(ws, row, COL_F, formatPercent(block?.[key]?.retentionRate), { align: "center" });
    row += 1;
  });

  return row;
}

function mergeBlock(ws, startRow, startCol, endRow, endCol, value, options = {}) {
  if (startRow !== endRow || startCol !== endCol) {
    ws.mergeCells(startRow, startCol, endRow, endCol);
  }
  setCell(ws, startRow, startCol, value, options);
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      if (r === startRow && c === startCol) continue;
      borderCell(ws.getCell(r, c));
    }
  }
}

function addSafetyIncidentsTable(ws, row, incidents, fyCurrent, fyPrevious) {
  mergeRow(ws, row, COL_D, COL_E, "Safety Incident/Number", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Category*", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  SAFETY_INCIDENTS.forEach(([incidentLabel, key]) => {
    const empData = incidents?.employees?.[key] || {};
    const workData = incidents?.workers?.[key] || {};
    const startRow = row;

    mergeBlock(ws, startRow, COL_D, startRow + 1, COL_E, incidentLabel, { align: "left", vertical: "top" });

    setCell(ws, row, COL_F, "Employees", { align: "center" });
    setCell(ws, row, COL_G, formatNumber(empData?.currentFinancialYear), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(empData?.previousFinancialYear), { align: "center" });
    row += 1;

    setCell(ws, row, COL_F, "Workers", { align: "center" });
    setCell(ws, row, COL_G, formatNumber(workData?.currentFinancialYear), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(workData?.previousFinancialYear), { align: "center" });
    row += 1;
  });

  return row;
}

function addRehabilitationTable(ws, row, leadership, fyCurrent, fyPrevious) {
  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  mergeRow(ws, row, COL_E, COL_F, "Total no. of affected employees/workers", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(
    ws,
    row,
    COL_G,
    COL_H,
    "No. of employees/workers that are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment",
    { bold: true, fill: TABLE_HEADER_FILL, align: "center" }
  );
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const current = leadership?.rehabilitatedEmploymentDetails?.currentFinancialYear || {};
  const previous = leadership?.rehabilitatedEmploymentDetails?.previousFinancialYear || {};

  [
    ["Employees", "employee"],
    ["Workers", "workers"],
  ].forEach(([label, key]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(current?.[key]?.total), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(previous?.[key]?.total), { align: "center" });
    setCell(ws, row, COL_G, formatNumber(current?.[key]?.rehabiliatated), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(previous?.[key]?.rehabiliatated), { align: "center" });
    row += 1;
  });

  return row;
}

function addComplaintsTable(ws, row, essential, fyLabel, yearKey) {
  row = addQuestionRow(ws, row, fyLabel, "", { fullWidth: true, bold: true });
  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_E, "Filed during the year", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Pending resolution at the end of year", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_G, COL_H, "Remarks", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const data = essential?.complaintsMadeByEmployeesAndWorkers?.[yearKey] || {};
  [
    ["Working Conditions", "workingCondition"],
    ["Health & Safety", "healthAndSafety"],
  ].forEach(([label, key]) => {
    const item = data?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(item?.filled), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(item?.pending), { align: "center" });
    const remark = safeText(item?.remark, "View Details");
    const hasRemark = item?.remark;
    mergeRow(ws, row, COL_G, COL_H, remark, {
      align: hasRemark ? "left" : "center",
    });
    row += 1;
  });

  return row;
}

export function buildPrinciple3Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 3", { views: [{ showGridLines: true }] });
  setPrinciple3ColumnWidths(ws);

  const principle = sectionC?.principle3 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const fyLabels = getFinancialYearLabels(reportingYear);
  const fyCurrent = fyLabels.currentYear ? `FY (${fyLabels.currentYear})` : "FY (Current Financial Year)";
  const fyPrevious = fyLabels.previousYear ? `PY (${fyLabels.previousYear})` : "PY (Previous Financial Year)";

  let row = 3;
  row = addSectionHeader(ws, row, `PRINCIPLE 3 ${PRINCIPLE_TITLE}`);
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addQuestionRow(
    ws,
    row,
    "1. a. Details of measures for the well-being of employees:",
    "",
    { fullWidth: true, bold: true }
  );
  row = addWellBeingBlock(ws, row, essential?.wellBeingMeasures, {
    sectionKey: "permanentEmployees",
    maleCountKey: "permanentEmployees",
    femaleCountKey: "permanentEmployees",
    blockLabel: "Permanent employees",
  });
  row = addWellBeingBlock(ws, row, essential?.wellBeingMeasures, {
    sectionKey: "otherEmployees",
    maleCountKey: "otherEmployees",
    femaleCountKey: "otherEmployees",
    blockLabel: "Other than Permanent employees",
  });

  row = addQuestionRow(
    ws,
    row,
    "1. b. Details of measures for the well-being of workers:",
    "",
    { fullWidth: true, bold: true }
  );
  row = addWellBeingBlock(ws, row, essential?.wellBeingWorkers, {
    sectionKey: "permanentWorkers",
    maleCountKey: "permanentWorkers",
    femaleCountKey: "permanentWorkers",
    blockLabel: "Permanent workers",
  });
  row = addWellBeingBlock(ws, row, essential?.wellBeingWorkers, {
    sectionKey: "otherWorkers",
    maleCountKey: "otherWorkers",
    femaleCountKey: "otherWorkers",
    blockLabel: "Other than Permanent workers",
  });

  row = addQuestionRow(
    ws,
    row,
    "1 c. Spending on measures towards well-being of employees and workers (including permanent and other than permanent) in the following format",
    "",
    { fullWidth: true, bold: true }
  );
  row = addQuestionRow(
    ws,
    row,
    "Cost incurred on wellbeing measures as a % of total revenue of the company",
    "",
    { fullWidth: true, bold: true }
  );

  mergeRow(ws, row, COL_D, COL_F, "In Current Financial", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(ws, row, COL_G, COL_I, "In Previous Financial Year", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;
  mergeRow(ws, row, COL_D, COL_F, formatPercent(essential?.wellBeingMeasuresCurrent), { align: "center" });
  mergeRow(ws, row, COL_G, COL_I, formatPercent(essential?.wellBeingMeasuresPrevious), { align: "center" });
  row += 1;

  row = addRetirementTable(ws, row, essential, fyCurrent, fyPrevious);

  const accessibilityAnswer = `${toYesNo(essential?.accessibilityUnderRPWD2016?.status)}${
    essential?.accessibilityUnderRPWD2016?.remark
      ? ` - ${safeText(essential.accessibilityUnderRPWD2016.remark)}`
      : ""
  }`;
  row = addQuestionRow(
    ws,
    row,
    "3. Are the premises / offices of the entity accessible to differently abled employees and workers, as per the requirements of the Rights of Persons with Disabilities Act, 2016? If not, whether any steps are being taken by the entity in this regard.",
    accessibilityAnswer,
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "left" }
  );

  const policy = essential?.rightsOfPersonsDisability2016 || {};
  const policyLink = policy?.webLink;
  const policyAnswer =
    policy?.status && policyLink
      ? policyLink
      : toYesNo(policy?.status);
  row = addQuestionRow(
    ws,
    row,
    "4. Does the entity have an equal opportunity policy as per the Rights of Persons with Disabilities Act, 2016? If so, provide a web-link to the policy.",
    policyAnswer,
    {
      valueCol: COL_VALUE_START,
      valueEnd: COL_VALUE_END,
      valueAlign: policyLink ? "left" : "center",
      link: policyLink || undefined,
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "5. Return to work and Retention rates of permanent employees and workers that took parental leave.",
    "",
    { fullWidth: true }
  );
  row = addReturnRetentionTable(
    ws,
    row,
    essential?.returnAndRetentionParentalLeave?.permanentEmployees,
    "Permanent Employees"
  );
  row = addReturnRetentionTable(
    ws,
    row,
    essential?.returnAndRetentionParentalLeave?.permanentWorkers,
    "Permanent Workers"
  );

  row = addQuestionRow(
    ws,
    row,
    "6. Is there a mechanism available to receive and redress grievances for the following categories of employees and worker? If yes, give details of the mechanism in brief.",
    "",
    { fullWidth: true }
  );

  GRIEVANCE_SEGMENTS.forEach(([label, key]) => {
    const item = essential?.grievanceRedressalMechanism?.[key] || {};
    const yesNo = toYesNo(item?.code);
    const remark = item?.remark ? String(item.remark).trim() : "";
    const value = remark ? `${yesNo} - ${remark}` : yesNo;
    row = addQuestionRow(ws, row, label, value, {
      valueCol: COL_VALUE_START,
      valueEnd: COL_VALUE_END,
      valueAlign: "left",
    });
  });

  row = addQuestionRow(
    ws,
    row,
    "7. Membership of employees and worker in association(s) or Unions recognized by the listed entity:",
    "",
    { fullWidth: true }
  );
  row = addQuestionRow(ws, row, "Permanent Employees", "", { fullWidth: true, bold: true });
  row = addMembershipTable(
    ws,
    row,
    essential?.membershipInAssociationsOrUnions?.permanentEmployees,
    fyCurrent,
    fyPrevious
  );
  row = addQuestionRow(ws, row, "Permanent Workers", "", { fullWidth: true, bold: true });
  row = addMembershipTable(
    ws,
    row,
    essential?.membershipInAssociationsOrUnions?.permanentWorkers,
    fyCurrent,
    fyPrevious
  );

  row = addQuestionRow(ws, row, "8. Details of training given to employees and workers:", "", { fullWidth: true });
  row = addQuestionRow(ws, row, "Employees", "", { fullWidth: true, bold: true });
  row = addTrainingTable(
    ws,
    row,
    essential?.trainingDetails?.currentFinancialYear?.employeeTrainingUpgradation,
    essential?.trainingDetails?.previousFinancialYear?.employeeTrainingUpgradation,
    fyCurrent,
    fyPrevious
  );
  row = addQuestionRow(ws, row, "Workers", "", { fullWidth: true, bold: true });
  row = addTrainingTable(
    ws,
    row,
    essential?.trainingDetails?.currentFinancialYear?.workerTrainingUpgradation,
    essential?.trainingDetails?.previousFinancialYear?.workerTrainingUpgradation,
    fyCurrent,
    fyPrevious
  );

  row = addQuestionRow(
    ws,
    row,
    "9. Details of performance and career development reviews of employees and worker:",
    "",
    { fullWidth: true }
  );
  row = addQuestionRow(ws, row, "a. Employees", "", { fullWidth: true, bold: true });
  row = addPerformanceTable(
    ws,
    row,
    essential?.performanceAndCareerReviews?.employee?.currentFinancialYear,
    fyCurrent
  );
  row = addPerformanceTable(
    ws,
    row,
    essential?.performanceAndCareerReviews?.employee?.previousFinancialYear,
    fyPrevious
  );
  row = addQuestionRow(ws, row, "b. Workers", "", { fullWidth: true, bold: true });
  row = addPerformanceTable(
    ws,
    row,
    essential?.performanceAndCareerReviews?.workers?.currentFinancialYear,
    fyCurrent
  );
  row = addPerformanceTable(
    ws,
    row,
    essential?.performanceAndCareerReviews?.workers?.previousFinancialYear,
    fyPrevious
  );

  row = addQuestionRow(ws, row, "10. Health and safety management system:", "", { fullWidth: true });
  row = addQuestionRow(
    ws,
    row,
    "a. Whether an occupational health and safety management system has been implemented by the entity? (Yes/ No). If yes, the coverage such system?",
    toYesNo(essential?.healthAndSafetyManagement?.occupationalHealthAndSafety?.status),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );
  if (essential?.healthAndSafetyManagement?.occupationalHealthAndSafety?.remark) {
    row = addTextOrDetailsRow(
      ws,
      row,
      "If yes, the coverage such system?",
      essential.healthAndSafetyManagement.occupationalHealthAndSafety.remark
    );
  } else if (essential?.healthAndSafetyManagement?.occupationalHealthAndSafety?.status) {
    row = addTextOrDetailsRow(ws, row, "If yes, the coverage such system?", "");
  }
  row = addQuestionRow(
    ws,
    row,
    "b. What are the processes used to identify work-related hazards and assess risks on a routine and non-routine basis by the entity?",
    safeText(essential?.healthAndSafetyManagement?.workRelatedHazardAssessment?.remark),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "left" }
  );
  row = addQuestionRow(
    ws,
    row,
    "c. Whether you have processes for workers to report the work related hazards and to remove themselves from such risks. (Y/N)?",
    toYesNo(essential?.healthAndSafetyManagement?.hazardReportingAndSelfRemovalProcess?.status),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );
  row = addQuestionRow(
    ws,
    row,
    "d. Do the employees/ workers of the entity have access to non-occupational medical and healthcare services? (Y/N)?",
    toYesNo(essential?.healthAndSafetyManagement?.nonOccupationalMedicalServicesAccess?.status),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );

  row = addQuestionRow(
    ws,
    row,
    "11. Details of safety related incidents, in the following format:",
    "",
    { fullWidth: true }
  );
  row = addSafetyIncidentsTable(ws, row, essential?.safetyIncidents, fyCurrent, fyPrevious);

  row = addTextOrDetailsRow(
    ws,
    row,
    "12. Describe the measures taken by the entity to ensure a safe and healthy work place.",
    essential?.safetyMeasures
  );

  row = addQuestionRow(
    ws,
    row,
    "13. Number of Complaints on the following made by employees and workers:",
    "",
    { fullWidth: true }
  );
  row = addComplaintsTable(ws, row, essential, fyCurrent, "currentFinancialYear");
  row = addComplaintsTable(ws, row, essential, fyPrevious, "previousFinancialYear");

  row = addQuestionRow(ws, row, "14. Assessments for the year:", "", { fullWidth: true });
  mergeRow(ws, row, COL_D, COL_E, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(
    ws,
    row,
    COL_F,
    COL_H,
    "% of your plants and offices that were assessed (by entity or statutory authorities or third parties)",
    { bold: true, fill: TABLE_HEADER_FILL, align: "center" }
  );
  row += 1;
  [
    ["Health and safety practices", essential?.assessmentYear?.healthAndSafetyPractice],
    ["Working Conditions", essential?.assessmentYear?.workingCondition],
  ].forEach(([label, value]) => {
    mergeRow(ws, row, COL_D, COL_E, label, { align: "left" });
    mergeRow(ws, row, COL_F, COL_H, formatPercent(value), { align: "center" });
    row += 1;
  });

  row = addTextOrDetailsRow(
    ws,
    row,
    "15. Provide details of any corrective action taken or underway to address safety-related incidents (if any) and on significant risks / concerns arising from assessments of health & safety practices and working conditions.",
    essential?.healthAndSafetyPracticesWorking
  );

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);

  const lifeInsurance = leadership?.lifeInsuranceOrCompensatoryPackage || {};
  row = addQuestionRow(
    ws,
    row,
    "1. Does the entity extend any life insurance or any compensatory package in the event of death of",
    "",
    { fullWidth: true }
  );
  row = addQuestionRow(
    ws,
    row,
    "(A) Employees (Y/N)",
    safeText(lifeInsurance?.male ?? lifeInsurance?.employee),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );
  row = addQuestionRow(
    ws,
    row,
    "(B) Workers (Y/N).",
    safeText(lifeInsurance?.female ?? lifeInsurance?.workers),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );

  row = addTextOrDetailsRow(
    ws,
    row,
    "2. Provide the measures undertaken by the entity to ensure that statutory dues have been deducted and deposited by the value chain partners.",
    leadership?.statutoryDuesDeductedDeposited
  );

  row = addQuestionRow(
    ws,
    row,
    "3. Provide the number of employees / workers having suffered high consequence work-related injury / ill-health / fatalities (as reported in Q11 of Essential Indicators above), who have been rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment:",
    "",
    { fullWidth: true }
  );
  row = addRehabilitationTable(ws, row, leadership, fyCurrent, fyPrevious);

  row = addQuestionRow(
    ws,
    row,
    "4. Does the entity provide transition assistance programs to facilitate continued employability and the management of career endings resulting from retirement or termination of employment? (Yes/ No/ NA)",
    safeText(leadership?.employabilityandmanagement),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );

  row = addQuestionRow(ws, row, "5. Details on assessment of value chain partners:", "", { fullWidth: true });
  mergeRow(ws, row, COL_D, COL_E, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(
    ws,
    row,
    COL_F,
    COL_H,
    "% of value chain partners (by value of business done with such partners) that were assessed",
    { bold: true, fill: TABLE_HEADER_FILL, align: "center" }
  );
  row += 1;
  [
    [
      "Health and safety practices",
      leadership?.assessmentOfValueChainPartners?.healthAndSafetyPractice ??
        leadership?.assessmentYear?.healthAndSafetyPractice,
    ],
    [
      "Working Conditions",
      leadership?.assessmentOfValueChainPartners?.workingCondition ??
        leadership?.assessmentYear?.workingCondition,
    ],
  ].forEach(([label, value]) => {
    mergeRow(ws, row, COL_D, COL_E, label, { align: "left" });
    mergeRow(ws, row, COL_F, COL_H, formatPercent(value), { align: "center" });
    row += 1;
  });

  addTextOrDetailsRow(
    ws,
    row,
    "6. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from assessments of health and safety practices and working conditions of value chain partners.",
    leadership?.valuechainpartners
  );

  return ws;
}
